import {
  type ComponentPropsWithRef,
  type ElementType,
  type ForwardedRef,
  forwardRef,
  type Ref,
  type ReactNode,
  type RefAttributes,
} from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '~/lib/utils';
import { Slot } from '@radix-ui/react-slot';

const defaultState =
  'whitespace-nowrap rounded cursor-pointer ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-[transform,background-color,color] [&:not(:disabled)]:active:translate-y-0.5 font-aspekta leading-none focus-visible:ring-2 focus-visible:ring-primary';
const disabledState = 'disabled:text-gray-400 disabled:cursor-not-allowed';

const buttonVariants = cva(`${defaultState} ${disabledState}`, {
  variants: {
    variant: {
      default:
        'bg-primary-button text-primary-button-foreground [&:not(:disabled)]:hover:bg-primary-button-hover [&:not(:disabled)]:focus:bg-primary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      destructive:
        'bg-destructive-button text-destructive-button-foreground [&:not(:disabled)]:hover:bg-destructive-button-hover [&:not(:disabled)]:focus:bg-destructive-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      success:
        'bg-secondary text-black [&:not(:disabled)]:hover:bg-secondary-hover [&:not(:disabled)]:focus:bg-secondary-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground',
      secondary:
        'border bg-secondary-button text-secondary-button-foreground border-secondary-button-border [&:not(:disabled)]:hover:bg-secondary-button-hover [&:not(:disabled)]:focus:bg-secondary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      tertiary:
        'bg-tertiary-button text-tertiary-button-foreground [&:not(:disabled)]:hover:bg-tertiary-button-hover [&:not(:disabled)]:focus:bg-tertiary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      outline:
        'border bg-secondary-button text-secondary-button-foreground border-secondary-button-border [&:not(:disabled)]:hover:bg-secondary-button-hover [&:not(:disabled)]:focus:bg-secondary-button-hover disabled:bg-disabled-button disabled:text-disabled-button-foreground disabled:border-disabled-button-border',
      ghost:
        'bg-transparent text-foreground [&:not(:disabled)]:hover:bg-primary-button [&:not(:disabled)]:focus:bg-primary [&:not(:disabled)]:hover:text-primary-button-foreground [&:not(:disabled)]:focus:text-primary-button-foreground',
      link: 'text-foreground underline-offset-2 underline [&:not(:disabled)]:hover:no-underline [&:not(:disabled)]:focus:no-underline',
    },
    size: {
      default: 'px-6 h-12',
      xs: 'text-xs px-3 h-6',
      sm: 'text-sm px-4 h-8',
      lg: 'px-8 h-14',
    },
    font: {
      primary: 'font-aspekta',
      secondary: 'font-spaceMono',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

type FixedForwardRef = <T, P = {}>(
  render: (props: P, ref: Ref<T>) => ReactNode,
  // @TODO: once we update to typescript 5.1 we can change to ReactNode from JSX.Element
) => (props: P & RefAttributes<T>) => JSX.Element;

const fixedForwardRef = forwardRef as FixedForwardRef;

export type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

export interface ButtonProps<TAs> extends VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  className?: string;
  as?: TAs;
}

const UnwrappedButton = <TAs extends ElementType>(
  {
    className,
    variant,
    size,
    asChild = false,
    as: Tag,
    font = 'primary',
    ...rest
  }: ButtonProps<TAs> & DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'button' : TAs>, 'as'>,
  ref: ForwardedRef<any>,
) => {
  // Support for asChild
  const Comp = asChild ? Slot : 'button';
  // support for as prop
  const Element = Tag ? Tag : Comp;
  // We need to make sure typescript sets a type using HTMLButtonElement props since its not optional
  const type = Comp === 'button' ? 'button' : undefined;

  // Display Link Buttons as inline elements
  const displayType = variant === 'link' ? 'inline' : 'inline-flex items-center justify-center';

  return (
    <Element
      className={cn(
        buttonVariants({
          variant,
          size,
          font,
        }),
        displayType,
        {
          'p-0': variant === 'link',
        },
        className,
      )}
      ref={ref}
      type={type}
      {...rest}
    />
  );
};

const Button = fixedForwardRef(UnwrappedButton);

export { Button, buttonVariants };
